import React, { useState, useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import Select from "react-select";
import axios from 'axios';

const EditItemSubCat = ({ show, onClose, itemSubId, onUpdated }) => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    const [categories, setCategories] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState(''); 
    const [errors, setErrors] = useState({});
    const [existingNameError, setExistingNameError] = useState(false);
    const [serverErrorMessage, setServerErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!show) return;

        const fetchData = async () => {
            try {
                // Fetch categories
                const catRes = await axios.get(`${baseURL}/item-categories/items/active`);
                const catData = Array.isArray(catRes.data) ? catRes.data : catRes.data.data;
                const mappedCategories = catData.map(cat => ({
                    value: cat.sno,
                    label: cat.itemcategory_name
                }));
                setCategories(mappedCategories);

                if (itemSubId) {
                    setLoading(true);
                    const itemRes = await axios.get(`${baseURL}/item-subcategories/${itemSubId}`);
                    const item = itemRes.data?.data; // ✅ Correctly access .data

                    setName(item.itemsubcategory_name || '');
                    setDescription(item.description || '');
                    const matchedCategory = mappedCategories.find(cat => cat.value === item.itemcategory_id);
                    setSelectedOption(matchedCategory || null);
                    setLoading(false);
                }
            } catch (err) {
                console.error("Error fetching data", err);
                setLoading(false);
            }
        };

        fetchData();
    }, [show, itemSubId]);

    const capitalizeFirstLetter = (str) => {
        return str.replace(/\b\w/g, char => char.toUpperCase());
    };

    const handleSubmit = () => {
        setErrors({});
        setExistingNameError(false);
        setServerErrorMessage('');

        const sanitizedName = name.trim();
        const sanitizedDescription = description.trim();

        const nameTooLong = sanitizedName.length > 300;
        const descTooLong = sanitizedDescription.length > 500;
        const nameRegex = /^[A-Za-z\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/;
        const numberRegex = /\d/;

        if (!selectedOption || !sanitizedName || nameTooLong || descTooLong) {
            setErrors({
                itemcategory_id: !selectedOption ? 'Item Category is required' : '',
                itemsubcategory_name: !sanitizedName
                    ? 'Subcategory name is required'
                    : nameTooLong
                        ? 'Subcategory name should be 300 characters max.'
                        : '',
                description: descTooLong ? 'Description should be 500 characters max.' : ''
            });
            return;
        }

        if (!nameRegex.test(sanitizedName)) {
            setErrors({ itemsubcategory_name: 'Subcategory name contains invalid characters' });
            return;
        }

        if (numberRegex.test(sanitizedName)) {
            setErrors({ itemsubcategory_name: 'Subcategory name should not contain numbers' });
            return;
        } 

        const finalName = capitalizeFirstLetter(sanitizedName);
        const finalDescription = capitalizeFirstLetter(sanitizedDescription);

        axios.put(`${baseURL}/item-subcategories/${itemSubId}`, {
            itemcategory_id: selectedOption.value,
            itemsubcategory_name: finalName,
            description: finalDescription
        })
            .then(() => {
                onClose();
                if (onUpdated) onUpdated();
            })
            .catch(err => {
                if (err.response?.status === 409) {
                    setExistingNameError(true);
                    setServerErrorMessage(err.response.data.message || 'Item Subcategory already exists');
                } else if (err.response?.status === 400) {
                    setErrors(err.response.data.errors || {});
                } else {
                    console.error("Update failed:", err);
                }
            });
    };
 
    return (
        <Modal show={show} onHide={onClose} size="md">
            <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#3ab384' }}> 
                    <h3 className="modal-title text-white">Update Item Sub Category</h3>
                    <Button variant="" className="close px-6 py-2" onClick={() =>{
                    setErrors({});
                    onClose();
                    }}>
                        <span className="text-white">×</span>
                    </Button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="text-center">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="row">
                            <div className="col-lg-12 mb-2">
                                <label className="text-black font-w500 fs-7">
                                    Item Category <span className="required">*</span>
                                </label>
                                <Select
                                    placeholder='Select Item Category'
                                    value={selectedOption}
                                    onChange={setSelectedOption}
                                    options={categories}
                                    classNamePrefix="react-select"
                                />
                                {errors.itemcategory_id && (
                                    <div className="text-danger">{errors.itemcategory_id}</div>
                                )}
                            </div>

                            <div className="col-lg-12 mb-2">
                                <label className="text-black font-w500 fs-7">
                                    Item Sub Category <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.itemsubcategory_name ? 'is-invalid' : ''}`}
                                    placeholder="Enter Item Sub Category"
                                    value={name}
                                    onChange={e => setName(capitalizeFirstLetter(e.target.value))}
                                />
                                {errors.itemsubcategory_name && (
                                    <div className="invalid-feedback">{errors.itemsubcategory_name}</div>
                                )}
                                {existingNameError && serverErrorMessage && (
                                    <div className="text-danger">{serverErrorMessage}</div>
                                )}
                            </div>

                            <div className="col-lg-12 mb-2">
                                <label className="text-black font-w500 fs-7">Description</label>
                                <textarea
                                    rows={4}
                                    className="form-control"
                                    placeholder="Description"
                                    value={description}
                                    onChange={e => setDescription(capitalizeFirstLetter(e.target.value))}
                                />
                                {errors.description && (
                                    <div className="text-danger">{errors.description}</div>
                                )}
                            </div> 
                        </div>
                    )}

                    <div className="d-flex justify-content-center gap-3 mt-4 mb-4">
                        <Button variant="secondary" size="sm" onClick={() =>{
                    setErrors({});
                    onClose();
                    }}>Cancel</Button>
                        <Button variant="primary" size="sm" onClick={handleSubmit}>Update Item Sub Category</Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default EditItemSubCat;
